import {
  BoxProps,
  Button,
  Container,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import { Cross, Share } from '@icon'
import { ISelectionsCollection, ISectionData } from '@interfaces'
import { useInViewMotion } from '@lib/hooks'
import { fadeIn } from '@lib/motion'
import { FadeInUpBox, IntersectionObserver, StaggerWrap } from '@motion'
import {
  MotionBox,
  MotionText,
  SelectionsFromCollectionCard,
  RichText,
} from '@ui'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'
import { useState } from 'react'
import Masonry from 'react-masonry-css'
import { RWebShare } from 'react-web-share'

interface ISelectionsFromWork {
  variant?: 'primary' | 'withPopUp'
  isDarkSection?: boolean
  sectionTitle: ISectionData['sectionTitle']
  sectionSubTitle: string
  sectionDescription?: string
  data: ISelectionsCollection[]
  footerButtonLabel?: string
  footerButtonOnPress?: () => void
  containerStyle?: BoxProps
  stickyId?: ISectionData['stickyId']
  isFooterButtonLoading?: boolean
}

const breakpointColumnsObj = {
  default: 3,
  1170: 3,
  768: 2,
  450: 1,
}

const getCardImageUrl = (imgUrl: string) => {
  if (!imgUrl) return imgUrl

  if (imgUrl.includes('/full')) {
    return imgUrl.replace('/full', '/preview')
  }

  return imgUrl
}

const SelectionsFromWork = ({
  variant = 'primary',
  data,
  sectionTitle,
  sectionSubTitle,
  sectionDescription,
  footerButtonLabel,
  footerButtonOnPress,
  containerStyle,
  stickyId = 'SelectionsFromWorks',
  isDarkSection = true,
  isFooterButtonLoading = false,
}: ISelectionsFromWork) => {
  const { t } = useTranslation()

  const [activePopUpIndex, setActivePopUpIndex] = useState<null | string>(null)
  const { isOpen, onToggle, onClose } = useDisclosure()
  const { controls, ref } = useInViewMotion(true)
  const router = useRouter()

  const handleOnClickForPopUp = (activeIndex: string) => {
    setActivePopUpIndex(activeIndex)
    onToggle()
  }

  const getActiveCollection = (): ISelectionsCollection =>
    data.find((collection) => collection.id === activePopUpIndex)

  return (
    <MotionBox
      pt={{ base: 'padding.primary', lg: 'padding.primary2' }}
      pb={{ base: '0', lg: 'padding.primary4' }}
      bgColor="black.primary4"
      id={stickyId}
      as="section"
      {...containerStyle}
    >
      <Container>
        <MotionBox
          maxW="610px"
          ref={ref}
          initial="hidden"
          animate={controls}
          variants={fadeIn.animationContainer}
        >
          {sectionSubTitle && (
            <MotionText
              variants={fadeIn.up}
              color="red.primary"
              fontSize={{ base: '2xl', lg: '3xl' }}
            >
              {sectionSubTitle}
            </MotionText>
          )}

          {sectionTitle && (
            <MotionText
              variants={fadeIn.up}
              color="rgba(178, 176, 171, 0.2)"
              fontSize={{ base: '4xl', lg: '7xl' }}
              fontWeight="bold"
            >
              {sectionTitle}
            </MotionText>
          )}

          {sectionDescription && (
            <MotionText
              variants={fadeIn.up}
              fontSize="md"
              fontWeight="light"
              color="white.primary"
            >
              {sectionDescription}
            </MotionText>
          )}
        </MotionBox>
      </Container>

      {/* GRID */}
      <Container maxW="container.secondary" mt="padding.primary">
        <StaggerWrap staggerChildren={1.6}>
          <Masonry
            breakpointCols={breakpointColumnsObj}
            className="masonry-grid"
            columnClassName="masonry-column"
          >
            {data &&
              data.length > 0 &&
              data.map((collection) => {
                if (variant === 'withPopUp') {
                  return (
                    <IntersectionObserver key={collection.id}>
                      <FadeInUpBox duration={1}>
                        <SelectionsFromCollectionCard
                          people={collection.people}
                          artist={collection.artist}
                          title={collection.title}
                          name={collection.name}
                          date={collection.date}
                          imgUrl={getCardImageUrl(collection.imgUrl)}
                          onClick={() => handleOnClickForPopUp(collection.id)}
                          isDarkSection={isDarkSection}
                          variant={variant}
                        />
                      </FadeInUpBox>
                    </IntersectionObserver>
                  )
                }

                return (
                  <IntersectionObserver key={collection.id}>
                    <FadeInUpBox duration={1}>
                      <SelectionsFromCollectionCard
                        people={collection.people}
                        artist={collection.artist}
                        url={collection.url}
                        title={collection.title}
                        name={collection.name}
                        date={collection.date}
                        nameUrl={collection?.nameUrl}
                        imgUrl={getCardImageUrl(collection.imgUrl)}
                        isDarkSection={isDarkSection}
                        variant={variant}
                      />
                    </FadeInUpBox>
                  </IntersectionObserver>
                )
              })}
          </Masonry>
        </StaggerWrap>
      </Container>

      {/* FOOTER BUTTON */}
      {footerButtonLabel && footerButtonOnPress && (
        <Button
          variant="primary"
          size="primary"
          bgColor="red.primary"
          onClick={footerButtonOnPress}
          mt={{ base: 'padding.primary', lg: 10 }}
          mb={{ base: '20px', lg: 0 }}
          isLoading={isFooterButtonLoading}
        >
          {footerButtonLabel}
        </Button>
      )}

      {/* MODAL */}
      {activePopUpIndex && (
        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay
            background="blackAlpha.primary80"
            sx={{ backdropFilter: 'blur(8px)' }}
          />
          <ModalContent
            mt={{ base: 'padding.primary', lg: '51px' }}
            maxW={{ base: 'calc(100vw - 60px)', lg: '620px' }}
            borderRadius="none"
            bgColor="white.primary"
          >
            <ModalHeader
              minH={{ base: '271px', lg: '267px' }}
              bgImage={`url(${
                getActiveCollection()?.imgUrl ||
                '/img/placeholders/no-image-available-icon.jpeg'
              })`}
              backgroundPosition="center"
              backgroundSize="cover"
              display="flex"
              justifyContent="space-between"
              alignItems="baseline"
            >
              <RWebShare
                data={{
                  text: getActiveCollection()?.title,
                  url: router.asPath,
                  title: `${getActiveCollection()?.title} | SSM`,
                }}
              >
                <Button variant="unstyled" rightIcon={<Share />}>
                  {t('visit:detailPage.share')}
                </Button>
              </RWebShare>

              <Button
                variant="unstyled"
                leftIcon={<Cross />}
                onClick={onClose}
              />
            </ModalHeader>

            <ModalBody>
              <Text fontSize="lg" color="red.primary" mb="22px">
                {getActiveCollection()?.title}
              </Text>

              <Text fontSize="sm" mb="6" fontWeight="medium">
                {getActiveCollection()?.subTitle}
              </Text>

              {getActiveCollection()?.description && (
                <RichText htmlContent={getActiveCollection()?.description} />
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      )}

      {/* CSS FIX (ÇOK ÖNEMLİ) */}
      <style jsx global>{`
        .masonry-grid {
          display: flex;
          margin-left: -28px;
          width: auto;
        }

        .masonry-column {
          padding-left: 28px;
          background-clip: padding-box;
        }

        .masonry-column > div {
          margin-bottom: 28px;
        }
      `}</style>
    </MotionBox>
  )
}

export default SelectionsFromWork
